import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { Session } from "@supabase/supabase-js";
import type { MeResponse } from "@screener/contracts";
import { getSupabase, hasSupabase } from "./supabase";
import { api, ApiFailure, setAccessTokenProvider } from "./api";

/**
 * 로그인 상태와 승인 상태를 한곳에서 관리한다.
 *
 * 승인 여부는 서버의 /api/v1/me 응답만 믿는다. 브라우저에서 판단하지
 * 않는다. 클라이언트 판단은 우회할 수 있고, 실제 차단은 DB 정책이 한다.
 */

export type SessionState =
  | { kind: "loading" }
  /** Supabase 미설정. 가상 샘플만 도는 상태. */
  | { kind: "skeleton" }
  | { kind: "anonymous" }
  /** 로그인했지만 초대·승인되지 않음 */
  | { kind: "unapproved" }
  | { kind: "member"; me: MeResponse };

interface SessionApi {
  state: SessionState;
  signIn: (email: string, password: string) => Promise<void>;
  sendMagicLink: (email: string) => Promise<void>;
  signOut: () => Promise<void>;
  refresh: () => Promise<void>;
}

const Ctx = createContext<SessionApi | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<SessionState>({ kind: "loading" });
  const [session, setSession] = useState<Session | null>(null);

  // API 클라이언트가 매 요청에서 최신 토큰을 읽어갈 수 있게 연결한다.
  useEffect(() => {
    setAccessTokenProvider(() => session?.access_token ?? null);
  }, [session]);

  const resolve = useCallback(async () => {
    if (!hasSupabase) {
      setState({ kind: "skeleton" });
      return;
    }
    try {
      const me = await api.me();
      if (me.mode === "skeleton") {
        setState({ kind: "skeleton" });
        return;
      }
      setState({ kind: "member", me });
    } catch (e) {
      if (e instanceof ApiFailure && e.status === 401) {
        setState({ kind: "anonymous" });
        return;
      }
      if (e instanceof ApiFailure && e.status === 403) {
        setState({ kind: "unapproved" });
        return;
      }
      // 그 밖의 실패는 로그인 상태를 단정할 수 없다. 익명으로 되돌린다.
      setState({ kind: "anonymous" });
    }
  }, []);

  useEffect(() => {
    const sb = getSupabase();
    if (!sb) {
      setState({ kind: "skeleton" });
      return;
    }
    let alive = true;

    sb.auth.getSession().then(({ data }) => {
      if (!alive) return;
      setSession(data.session ?? null);
    });

    const { data: sub } = sb.auth.onAuthStateChange((_event, next) => {
      setSession(next ?? null);
    });

    return () => {
      alive = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  // 세션이 정해진 뒤에 서버에 승인 상태를 묻는다.
  useEffect(() => {
    if (!hasSupabase) return;
    if (session === null) {
      // getSession 이 아직 끝나지 않았을 수도 있으므로 한 번 물어본다.
      void resolve();
      return;
    }
    void resolve();
  }, [session, resolve]);

  const value = useMemo<SessionApi>(
    () => ({
      state,
      async signIn(email, password) {
        const sb = getSupabase();
        if (!sb) throw new Error("인증이 구성되지 않았다");
        const { error } = await sb.auth.signInWithPassword({ email, password });
        if (error) throw new Error(error.message);
      },
      async sendMagicLink(email) {
        const sb = getSupabase();
        if (!sb) throw new Error("인증이 구성되지 않았다");
        const { error } = await sb.auth.signInWithOtp({
          email,
          options: { shouldCreateUser: false },
        });
        if (error) throw new Error(error.message);
      },
      async signOut() {
        const sb = getSupabase();
        if (!sb) return;
        await sb.auth.signOut();
        setState({ kind: "anonymous" });
      },
      refresh: resolve,
    }),
    [state, resolve],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useSession(): SessionApi {
  const v = useContext(Ctx);
  if (!v) throw new Error("SessionProvider 안에서만 쓸 수 있다");
  return v;
}
